import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { API, useOrg } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Calculator, Trash2, Edit2 } from "lucide-react";

const TaxesPage = () => {
  const { currentOrg } = useOrg();
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [form, setForm] = useState({
    name: "",
    tax_type: "percentage",
    rate: "",
    applies_per: "line",
    default_for_items: false,
    jurisdiction: ""
  });

  const fetchTaxes = async () => {
    if (!currentOrg) return;
    try {
      const response = await axios.get(`${API}/taxes`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      setTaxes(response.data);
    } catch (error) {
      console.error("Failed to fetch taxes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTaxes();
  }, [currentOrg]);

  const resetForm = () => {
    setForm({
      name: "",
      tax_type: "percentage",
      rate: "",
      applies_per: "line",
      default_for_items: false,
      jurisdiction: ""
    });
    setEditingTax(null);
  };

  const handleEdit = (tax) => {
    setEditingTax(tax);
    setForm({
      name: tax.name,
      tax_type: tax.tax_type,
      rate: tax.rate.toString(),
      applies_per: tax.applies_per,
      default_for_items: tax.default_for_items,
      jurisdiction: tax.jurisdiction || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      rate: parseFloat(form.rate)
    };

    try {
      if (editingTax) {
        await axios.put(`${API}/taxes/${editingTax.tax_id}`, payload, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        toast.success("Tax rule updated");
      } else {
        await axios.post(`${API}/taxes`, payload, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        toast.success("Tax rule created");
      }
      await fetchTaxes();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (taxId) => {
    if (!confirm("Are you sure you want to delete this tax rule?")) return;
    try {
      await axios.delete(`${API}/taxes/${taxId}`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      toast.success("Tax rule deleted");
      await fetchTaxes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="taxes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-medium text-zinc-900">Tax Management</h1>
          <p className="text-muted-foreground text-sm">Configure tax rules for your invoices</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-tax-btn">
              <Plus className="w-4 h-4" />
              Add Tax Rule
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTax ? "Edit Tax Rule" : "Create Tax Rule"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Tax Name *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="e.g., KY Vape Tax, Sales Tax"
                  required
                  data-testid="tax-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tax Type</Label>
                  <Select value={form.tax_type} onValueChange={(v) => setForm({...form, tax_type: v})}>
                    <SelectTrigger data-testid="tax-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="rate">Rate *</Label>
                  <Input
                    id="rate"
                    type="number"
                    step="0.01"
                    value={form.rate}
                    onChange={(e) => setForm({...form, rate: e.target.value})}
                    placeholder={form.tax_type === 'percentage' ? "e.g., 6" : "e.g., 1.50"}
                    required
                    data-testid="tax-rate-input"
                  />
                </div>
              </div>
              <div>
                <Label>Applies Per</Label>
                <Select value={form.applies_per} onValueChange={(v) => setForm({...form, applies_per: v})}>
                  <SelectTrigger data-testid="applies-per-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unit">Per Unit (pcs/box/case)</SelectItem>
                    <SelectItem value="line">Per Line Item</SelectItem>
                    <SelectItem value="invoice">Per Invoice</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {form.applies_per === 'unit' && "Tax calculated for each unit (e.g., $1.50 × quantity)"}
                  {form.applies_per === 'line' && "Tax calculated once per line item"}
                  {form.applies_per === 'invoice' && "Tax calculated once for the entire invoice"}
                </p>
              </div>
              <div>
                <Label htmlFor="jurisdiction">Jurisdiction</Label>
                <Input
                  id="jurisdiction"
                  value={form.jurisdiction}
                  onChange={(e) => setForm({...form, jurisdiction: e.target.value})}
                  placeholder="e.g., Kentucky, Federal"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.default_for_items}
                  onCheckedChange={(v) => setForm({...form, default_for_items: v})}
                />
                <Label>Default for new items</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="tax-submit-btn">
                  {editingTax ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tax Rules List */}
      <div className="bg-white border border-zinc-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : taxes.length === 0 ? (
          <div className="p-16 text-center">
            <Calculator className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-muted-foreground mb-2">No tax rules configured</p>
            <p className="text-sm text-muted-foreground">
              Add tax rules to automatically calculate taxes on your invoices
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Tax Name</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Type</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Rate</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Applies Per</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Jurisdiction</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {taxes.map((tax) => (
                  <tr key={tax.tax_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{tax.name}</span>
                        {tax.default_for_items && (
                          <span className="bg-primary/10 text-primary text-xs px-2 py-0.5">Default</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium ${
                        tax.tax_type === 'percentage' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                      }`}>
                        {tax.tax_type === 'percentage' ? 'Percentage' : 'Fixed'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-medium">
                      {tax.tax_type === 'percentage' ? `${tax.rate}%` : `$${tax.rate.toFixed(2)}`}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground capitalize">{tax.applies_per}</td>
                    <td className="py-3 px-4 text-muted-foreground">{tax.jurisdiction || '-'}</td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(tax)}
                        data-testid={`edit-tax-${tax.tax_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(tax.tax_id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-accent/30 border border-accent p-6">
        <h3 className="font-outfit font-medium mb-2">About Per-Unit Taxes</h3>
        <p className="text-sm text-muted-foreground">
          Per-unit taxes (like the KY Vape Tax) are calculated based on quantity. For example, a $1.50/unit tax on 5 items = $7.50 in tax.
          This is different from percentage taxes which are calculated on the line total.
        </p>
      </div>
    </div>
  );
};

export default TaxesPage;
